/* (c) 2020-2023, Nextwave Logic, Inc. All Rights Reserved */
import React from "react";
import Logo from "../../assets/UW_Horizontal_Logo_Full.png";
import { use } from "passport";

const styles = {
  // Layout & Container styles
  layout: {
    container: {
      padding: "20px",
      maxWidth: "210mm", // A4 width
      margin: "0 auto",
      backgroundColor: "#ffffff"
    },
    mainContainer: {
      width: "100%",
      marginTop: "20px"
    },
    newPage: {
      pageBreakBefore: "always",
      paddingTop: "40px",
      pageBreakInside: "avoid"
    }
  },

  // Header styles
  header: {
    wrapper: {
      display: "flex",
      flexDirection: "column",
      marginBottom: "30px"
    },
    topRow: {
      display: "flex",
      justifyContent: "space-between",
      alignItems: "center",
      marginBottom: "20px"
    },
    logo: {
      width: "200px",
      height: "auto",
      objectFit: "contain"
    },
    candidateInfo: {
      fontSize: "14px",
      color: "#555",
      textAlign: "right",
      marginBottom: "4px"
    },
    title: {
      textAlign: "center",
      fontSize: "28px",
      color: "#008C68",
      fontWeight: "bold",
      margin: "0 auto"
    }
  },

  // Section styles
  section: {
    title: {
      fontSize: "24px",
      color: "#008C68",
      marginBottom: "30px",
      fontWeight: "bold"
    },
    description: {
      fontSize: "14px",
      marginBottom: "30px",
      lineHeight: "1.6"
    },
    container: {
      marginBottom: "60px",
      padding: "0 20px",
      pageBreakInside: "avoid"
    }
  },

  // Score styles
  scores: {
    item: {
      fontSize: "14px",
      marginBottom: "25px",
      color: "#01A9BE"
    },
    container: {
      marginBottom: "40px",
      padding: "20px",
      backgroundColor: "#f8f8f8",
      borderRadius: "8px"
    },
    progressBar: {
      wrapper: {
        width: "100%",
        backgroundColor: "#e0e0e0",
        height: "15px",
        marginTop: "8px",
        borderRadius: "5px",
        overflow: "hidden"
      },
      fill: {
        backgroundColor: "#008C68",
        height: "100%"
      }
    }
  },

  // Table styles
  table: {
    wrapper: {
      width: "100%",
      borderCollapse: "collapse",
      marginBottom: "30px",
      tableLayout: "fixed"
    },
    header: {
      border: "1px solid #888",
      padding: "15px",
      fontSize: "14px",
      color: "#FFFFFF",
      backgroundColor: "#00CC97",
      textAlign: "center",
      fontWeight: "bold"
    },
    cell: {
      border: "1px solid #888",
      padding: "18px",
      fontSize: "14px",
      verticalAlign: "top",
      wordWrap: "break-word",
      lineHeight: "1.6"
    },
    headerTitle: {
      borderBottom: "2px solid #888",
      textAlign: "center",
      fontSize: "20px",
      color: "#008C68",
      padding: "15px"
    }
  },

  // Interest section styles
  interests: {
    list: {
      listStyleType: "none",
      padding: "20px",
      backgroundColor: "#f8f8f8",
      borderRadius: "8px",
      fontSize: "14px",
      marginBottom: "30px"
    }
  },

  // New styles for encapsulation section
  skillBox: {
    border: "1px solid #008C68",
    borderRadius: "8px",
    padding: "20px",
    marginBottom: "30px",
    pageBreakInside: "avoid"
  },
  skillBoxTitle: {
    color: "#008C68",
    fontSize: "14px",
    fontWeight: "bold",
    marginBottom: "15px",
    textAlign: "center"
  },
  skillList: {
    paddingLeft: "25px",
    margin: 0,
    fontSize: "14px",
    lineHeight: "1.6",
    '& li': {
      fontSize: "14px"
    }
  }
};

// Helper component for progress bars
const ProgressBar = ({ score, maxScore = 5 }) => (
  <div style={styles.scores.progressBar.wrapper}>
    <div style={{
      ...styles.scores.progressBar.fill,
      width: `${(score / maxScore) * 100}%`
    }}></div>
  </div>
);

// Helper component for trait scores
const TraitScore = ({ trait, score }) => (
  <li key={trait} style={styles.scores.item}>
    <strong>{trait}:</strong> {score}/5
    <ProgressBar score={score} />
  </li>
);

const PsychometricTestPDFLayout = ({ userData }) => {


  // All the trait descriptions and content
  const traitContent = {
    Extraversion: {
      description: "Extraversion measures how outgoing and socially engaged an individual is. It reflects a person's comfort level in social situations and their tendency to seek out interactions with others.",
      highScore: "Individuals who score high on Extraversion are typically sociable, energetic, and assertive. They thrive in social settings, enjoy engaging with others, and often have a large social network. Their enthusiasm and positive emotions make them effective in roles that require teamwork and collaboration.",
      lowScore: "Those who score low on Extraversion, often referred to as introverts, may prefer solitude and feel drained after social interactions. They tend to be more reserved, may avoid large groups, and often need time alone to recharge. This can make them well-suited for roles that require independent work or deep focus."
    },
    Openness: {
      description: "Openness to Experience reflects the degree of intellectual curiosity, creativity, and a preference for novelty and variety. Individuals high in this trait are often more willing to embrace new ideas and experiences, making them adaptable in various situations.",
      highScore: "Individuals who score high on Openness are typically imaginative, curious, and open to new experiences. They enjoy exploring new concepts, are more creative, and often think about abstract ideas. They are likely to seek out challenges and have a wide range of interests, including travel and learning.",
      lowScore: "Those who score low on Openness may prefer routine and familiarity, showing less interest in new experiences. They tend to be more traditional in their thinking and may resist change, often sticking to established methods and practices."
    },
    Agreeableness: {
      description: "Agreeableness describes how individuals interact with others and their tendency toward compassion and cooperation. It reflects a person's concern for social harmony and their ability to empathize with others.",
      highScore: "Individuals high in Agreeableness are typically kind, empathetic, and cooperative. They value social harmony and are often seen as trustworthy and caring. Their ability to work well with others makes them effective in team-oriented roles and positions that require negotiation or conflict resolution.",
      lowScore: "Those who score low on Agreeableness may be more competitive, critical, or skeptical of others. They might prioritize their own interests over social harmony, which can lead to conflicts in collaborative environments. While this trait can be beneficial in competitive roles, it may hinder teamwork and cooperation."
    },
    Neuroticism: {
      description: "Neuroticism refers to an individual's emotional stability and their tendency to experience negative emotions. It reflects how a person handles stress and emotional challenges.",
      highScore: "Individuals who score high on Neuroticism often experience feelings of anxiety, insecurity, and moodiness. They may be more sensitive to stress and prone to worry, which can affect their overall emotional well-being and performance in high-pressure situations.",
      lowScore: "Those who score low on Neuroticism, indicating high emotional stability, are generally more resilient and able to manage stress effectively. They tend to remain calm under pressure, are less likely to experience negative emotions, and often approach challenges with a positive mindset. This trait is advantageous in roles that involve high-stress environments."
    },
    Conscientiousness: {
      description: "Conscientiousness refers to how organized, dependable, and goal-oriented an individual is. This trait encompasses a person's ability to manage their impulses, stay focused on tasks, and maintain a structured approach to their work.",
      highScore: "Individuals with high Conscientiousness are typically well-organized, disciplined, and reliable. They are good at planning, meeting deadlines, and maintaining a strong work ethic. Their attention to detail and goal-driven nature makes them effective in roles that require responsibility and thoroughness.",
      lowScore: "Those who score low on Conscientiousness may struggle with organization and time management. They tend to be more impulsive, less structured, and may procrastinate or have difficulty focusing on long-term goals. This can lead to challenges in roles that require consistency and reliability."
    }
  };

  // First create the skills content object similar to traitContent
  const skillsContent = {
    Human_Skill: {
      description: "The individual is able to interact effectively with others. He/she is typically skilled at communication, problem solving, managing relationships, teamwork and leadership. He/she tends to have abilities of being patient, positive and are able to engage with customers or clients easily. He/she will be having an encompassing personality of making people feel that they know them personally.",
      highScore: "He/she tends to have abilities of being patient, positive and are able to engage with customers or clients easily.",
      lowScore: "He/she will be having an encompassing personality of making people feel that they know them personally."
    },
    Conceptual_Skill: {
      description: "The individual is able to have critical thinking and an ability of having holistic perspective of a complex situations. He/she tends to have creative thinking, strategic planning, and grasping abstract concept. He/she tends to be active listener with analytical mindset and decision-making skills. He/she tends to display leadership as well as managerial skills coupled with problem solving traits.",
      highScore: "He/she tends to have creative thinking, strategic planning, and grasping abstract concept. He/she tends to be active listener with analytical mindset and decision-making skills.",
      lowScore: "He/she tends to display leadership as well as managerial skills coupled with problem solving traits."
    },
    Technical_Skill: {
      description: "Technical skills are specialized abilities needed to perform specific tasks in real-world situations. Regardless of the industry, knowledge of certain technical skills is necessary to meet daily work performance requirements. The individual needs to acquire abilities by using and gaining expertise in performing either physical or digital tasks.",
      highScore: "Strong proficiency in technical areas such as programming languages, common operating systems, and software proficiency.",
      lowScore: "May need development in areas such as technical writing, project management, and data analysis."
    }
  };

  const renderTraitSection = (trait) => (
    <div style={styles.layout.newPage}>
      <div style={{ 
        marginBottom: "20px",
        height: "130px",
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between"
      }}>
        <img 
          src={`/assets/${trait}PDFLogo.png`}
          alt={trait} 
          style={{ 
            height: "100%",
            width: "auto",
            objectFit: "contain",
            marginLeft: "20px"
          }} 
        />
        <div style={{
          fontSize: "24px",
          color: "#01A9BE",
          fontWeight: "bold",
          marginRight: "40px"
        }}>
          Score: {userData?.traits_scores?.[trait] ?? 0}/5
        </div>
      </div>
      
      <p style={styles.section.description}>
        <strong style={{ color: "#01A9BE" }}>{trait}:</strong> {traitContent[trait].description}
      </p>
      <p style={styles.section.description}>
        <strong style={{ color: "#01A9BE" }}>High score:</strong> {traitContent[trait].highScore}
      </p>
      <p style={styles.section.description}>
        <strong style={{ color: "#01A9BE" }}>Low score:</strong> {traitContent[trait].lowScore}
      </p>

      {/* Tables for all personality traits */}
      {(trait === "Extraversion" || trait === "Openness" || trait === "Agreeableness" || 
        trait === "Neuroticism" || trait === "Conscientiousness") && (
        <table style={{
          ...styles.table.wrapper,
          marginTop: "30px"
        }}>
          <thead>
            <tr>
              <th style={{
                ...styles.table.header,
                width: "50%"
              }}>High</th>
              <th style={{
                ...styles.table.header,
                width: "50%"
              }}>Low</th>
            </tr>
          </thead>
          <tbody>
            {trait === "Extraversion" ? (
              <>
                <tr>
                  <td style={styles.table.cell}>Enjoys being the centre of attention but say things before thinking about them</td>
                  <td style={styles.table.cell}>Prefers solitude and dislikes making small talk as well as dislikes being center of attention</td>
                </tr>
                <tr>
                  <td style={styles.table.cell}>Likes to start conversation and enjoy meeting new people</td>
                  <td style={styles.table.cell}>Feels exhausted when having to socialize a lot</td>
                </tr>
                <tr>
                  <td style={styles.table.cell}>Has wide social circle of friends and acquaintances</td>
                  <td style={styles.table.cell}>Finds it difficult to start conversation</td>
                </tr>
                <tr>
                  <td style={styles.table.cell}>Finds it easy to make new friends and feels energized when around with other people</td>
                  <td style={styles.table.cell}>Carefully thinks things through before speaking</td>
                </tr>
              </>
            ) : trait === "Openness" ? (
              <>
                <tr>
                  <td style={styles.table.cell}>Very creative</td>
                  <td style={styles.table.cell}>Dislikes change and abstract or theoretical concepts</td>
                </tr>
                <tr>
                  <td style={styles.table.cell}>Open to trying new challenges</td>
                  <td style={styles.table.cell}>Does not enjoy new things</td>
                </tr>
                <tr>
                  <td style={styles.table.cell}>Happy to think about abstract concepts</td>
                  <td style={styles.table.cell}>Revisits new ideas</td>
                </tr>
                <tr>
                  <td style={styles.table.cell}>Focused on tackling new challenges</td>
                  <td style={styles.table.cell}>Not very imaginative</td>
                </tr>
              </>
            ) : trait === "Agreeableness" ? (
              <>
                <tr>
                  <td style={styles.table.cell}>Has a great deal of interest in other people</td>
                  <td style={styles.table.cell}>Takes little interest in others and doesn't care about how other people feel</td>
                </tr>
                <tr>
                  <td style={styles.table.cell}>Cares about others and assists other who are in need of help</td>
                  <td style={styles.table.cell}>Has little interest in other people's problems</td>
                </tr>
                <tr>
                  <td style={styles.table.cell}>Feels empathy and concern for other people</td>
                  <td style={styles.table.cell}>Insults and belittles others</td>
                </tr>
                <tr>
                  <td style={styles.table.cell}>Enjoys helping and contributing to the happiness of other people</td>
                  <td style={styles.table.cell}>Manipulates others to get what they want</td>
                </tr>
              </>
            ) : trait === "Neuroticism" ? (
              <>
                <tr>
                  <td style={styles.table.cell}>Experiences a lot of stress and feels anxious</td>
                  <td style={styles.table.cell}>Emotionally stable</td>
                </tr>
                <tr>
                  <td style={styles.table.cell}>Worries about many different things and gets upset easily</td>
                  <td style={styles.table.cell}>Deals well with stress</td>
                </tr>
                <tr>
                  <td style={styles.table.cell}>Experiences dramatic shifts in mood</td>
                  <td style={styles.table.cell}>Rarely feels sad or depressed</td>
                </tr>
                <tr>
                  <td style={styles.table.cell}>Struggles to bounce back after stressful events</td>
                  <td style={styles.table.cell}>Doesn't worry much and is very relaxed</td>
                </tr>
              </>
            ) : (
              <>
                <tr>
                  <td style={styles.table.cell}>Spends time preparing</td>
                  <td style={styles.table.cell}>Dislikes structure and schedules</td>
                </tr>
                <tr>
                  <td style={styles.table.cell}>Finishes important tasks right away</td>
                  <td style={styles.table.cell}>Makes messes and doesn't like to take care of things</td>
                </tr>
                <tr>
                  <td style={styles.table.cell}>Pays attention to detail</td>
                  <td style={styles.table.cell}>Fails to return things or put them back where they belong</td>
                </tr>
                <tr>
                  <td style={styles.table.cell}>Enjoys having a set schedule</td>
                  <td style={styles.table.cell}>Procrastinates important tasks and fails to complete necessary or assigned tasks</td>
                </tr>
              </>
            )}
          </tbody>
        </table>
      )}
    </div>
  );

  const renderSkillScores = (skill, score) => (
    <li key={skill} style={styles.scores.item}>
      <strong>{skill.replace("_", " ")}:</strong> {score}/30
      <div style={styles.scores.progressBar.wrapper}>
        <div style={{
          ...styles.scores.progressBar.fill,
          width: `${(score / 30) * 100}%`
        }}></div>
      </div>
    </li>
  );

  const renderSkillSection = (skill) => (
    <div style={styles.layout.newPage}>
      <div style={{ 
        marginBottom: "20px",
        height: "130px",
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between"
      }}>
        <img 
          src={`/assets/${skill.split('_')[0]}PDFLogo.png`}
          alt={skill} 
          style={{ 
            height: "100%",
            width: "auto",
            objectFit: "contain",
            marginLeft: "20px"
          }} 
        />
        <div style={{
          fontSize: "24px",
          color: "#01A9BE",
          fontWeight: "bold",
          marginRight: "40px"
        }}>
          Score: {userData?.skills_scores?.[skill] ?? 0}/30
        </div>
      </div>
      
      <p style={styles.section.description}>
        <strong style={{ color: "#01A9BE" }}>{skill.replace('_', ' ')}:</strong> {skillsContent[skill].description}
      </p>
      <p style={styles.section.description}>
        <strong style={{ color: "#01A9BE" }}>High score:</strong> {skillsContent[skill].highScore}
      </p>
      <p style={styles.section.description}>
        <strong style={{ color: "#01A9BE" }}>Low score:</strong> {skillsContent[skill].lowScore}
      </p>

      {/* Boxes for all skill types */}
      {(skill === "Human_Skill" || skill === "Conceptual_Skill" || skill === "Technical_Skill") && (
        <div style={{
          border: "1px solid #008C68",
          borderRadius: "8px",
          padding: "15px",
          marginTop: "20px"
        }}>
          <div style={{
            textAlign: "center",
            fontSize: "18px",
            color: "#008C68",
            fontWeight: "bold",
            marginBottom: "15px"
          }}>
            {skill.replace('_', ' ')}s
          </div>
          <div style={{
            display: "grid",
            gridTemplateColumns: "auto 1fr",
            gap: "8px",
            fontSize: "14px",
            lineHeight: "1.4"
          }}>
            {skill === "Human_Skill" ? (
              <>
                <div>1.</div><div>Leadership</div>
                <div>2.</div><div>Strategic and leadership thinking</div>
                <div>3.</div><div>Adaptability</div>
                <div>4.</div><div>Ownership</div>
                <div>5.</div><div>Decisiveness</div>
                <div>6.</div><div>Organized workflow</div>
                <div>7.</div><div>Effective communication</div>
                <div>8.</div><div>Active listening</div>
                <div>9.</div><div>Empathy and compassion</div>
                <div>10.</div><div>Problem solver</div>
              </>
            ) : skill === "Conceptual_Skill" ? (
              <>
                <div>1.</div><div>Abstract thinking</div>
                <div>2.</div><div>Strategic thinking</div>
                <div>3.</div><div>Problem solving</div>
                <div>4.</div><div>Big picture vision</div>
                <div>5.</div><div>Critical thinking</div>
                <div>6.</div><div>Innovation</div>
                <div>7.</div><div>Creative thinking</div>
              </>
            ) : (
              <>
                <div>1.</div><div>Inquisitive</div>
                <div>2.</div><div>Advisory skills</div>
                <div>3.</div><div>Problem solving</div>
                <div>4.</div><div>Customer service skills</div>
                <div>5.</div><div>Mechanical aptitude</div>
                <div>6.</div><div>Electrical skills</div>
                <div>7.</div><div>Detailed orientation</div>
                <div>8.</div><div>Analytical skills</div>
                <div>9.</div><div>Software skills</div>
                <div>10.</div><div>Programming Language (IT skills)</div>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );

  // Update the skills section rendering
  const renderSkillsOverview = () => (
    <div style={styles.layout.newPage}>
      <h3 style={styles.section.title}>C. Skills Scores</h3>
      
      <div style={{
        padding: "15px",
        backgroundColor: "#f8f8f8",
        borderRadius: "8px",
        marginBottom: "20px"
      }}>
        <ul style={{ listStyleType: "none", padding: 0, margin: 0 }}>
          {["Technical_Skill", "Human_Skill", "Conceptual_Skill"].map(skill =>
            renderSkillScores(skill, userData?.skills_scores?.[skill] ?? 0)
          )}
        </ul>
      </div>
      
      <table style={{...styles.table.wrapper, marginBottom: "20px"}}>
        <thead>
          <tr>
            <th colSpan="2" style={styles.table.headerTitle}>
              Scoring Interpretation
            </th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td style={{ padding: "12px", fontSize: "16px" }}>
              6-13 = Low Range
            </td>
          </tr>
          <tr>
            <td style={{ padding: "12px", fontSize: "16px" }}>
              14-22 = Moderate Range
            </td>
          </tr>
          <tr>
            <td style={{ padding: "12px", fontSize: "16px" }}>
              23-30 = High Range
            </td>
          </tr>
        </tbody>
      </table>
    </div>
  );

  const renderSkillsDetails = () => (
    <div style={styles.layout.newPage}>
      <h3 style={styles.section.title}>Skills Descriptions</h3>
      
      <div style={{ marginBottom: 0 }}>
        <p style={{...styles.section.description, marginBottom: "15px"}}>
          <strong style={{ color: "#01A9BE" }}>Human Skills:</strong> The individual is able to interact effectively 
          with others. He/she is typically skilled at communication, problem solving, managing relationships, teamwork 
          and leadership. He/she tends to have abilities of being patient, positive and are able to engage with 
          customers or clients easily. He/she will be having an encompassing personality of making people feel that 
          they know them personally.
        </p>

        <p style={{...styles.section.description, marginBottom: "15px"}}>
          <strong style={{ color: "#01A9BE" }}>Conceptual Skills:</strong> The individual is able to have critical 
          thinking and an ability of having holistic perspective of a complex situations. He/she tends to have 
          creative thinking, strategic planning, and grasping abstract concept. He/she tends to be active listener 
          with analytical mindset and decision-making skills. He/she tends to display leadership as well as 
          managerial skills coupled with problem solving traits.
        </p>

        <p style={{...styles.section.description, marginBottom: 0}}>
          <strong style={{ color: "#01A9BE" }}>Technical Skills:</strong> The individual needs to acquire abilities by 
          using and gaining expertise in performing either physical or digital tasks. Example of technical skills are: 
          a. Programming languages, b. Common operating systems, c. Software proficiency, d. Technical writing, 
          e. Project management, f. Data analysis
        </p>
      </div>
    </div>
  );

  // Add this new function to render the encapsulation section
  const renderEncapsulation = () => (
    <div style={styles.layout.newPage}>
      <h2 style={{
        fontSize: "24px",
        color: "#008C68",
        textAlign: "center",
        marginBottom: "30px",
        fontWeight: "bold"
      }}>
        IN CAPSULATION
      </h2>

      <p style={{
        fontSize: "14px",
        lineHeight: "1.6",
        marginBottom: "30px",
        textAlign: "justify"
      }}>
        Dear participants after recording your responses of the entire above questionnaire compare
        your scores and interpretation of these three areas: Your Personality, Your Interest and Your
        Skills kindly go through the below seven lists of detailed and specified skills according to its
        areas for your knowledge.
      </p>

      {/* All skill boxes with pageBreakInside: avoid */}
      <div style={styles.skillBox}>
        <h3 style={styles.skillBoxTitle}>Number Skills</h3>
        <div style={{
          paddingLeft: "25px",
          margin: 0,
          fontSize: "14px",
          lineHeight: "1.6"
        }}>
          {[
            "Compute, calculate, compare or record numbers",
            "Forecast, appraise or estimate numerical information",
            "Doing arithmetic correctly",
            "Using percentages and decimals",
            "Estimating costs and/or time needed to complete a job",
            "Using a database program on a computer",
            "Using a spreadsheet on a computer",
            "Creating and managing a budget"
          ].map((item, index) => (
            <div key={index} style={{ marginBottom: "8px", fontSize: "14px" }}>
              {index + 1}. {item}
            </div>
          ))}
        </div>
      </div>

      {/* Business Skills */}
      <div style={{
        ...styles.skillBox,
        marginBottom: "200px"
      }}>
        <h3 style={styles.skillBoxTitle}>Business Skills</h3>
        <div style={{
          paddingLeft: "25px",
          margin: 0,
          fontSize: "14px",
          lineHeight: "1.6"
        }}>
          {[
            "Working with computers",
            "Using a business telephone",
            "Working with budgets",
            "Account, budget, program or systematize financial data",
            "Attend to detail, copy, inspect or transcribe",
            "Setting up and closing out a cash register",
            "Managing money and bills",
            "Organizing, filing, updating, categorizing or arranging information",
            "Writing business documents",
            "Coordinating events Fund-raising"
          ].map((item, index) => (
            <div key={index} style={{ marginBottom: "8px", fontSize: "14px" }}>
              {index + 1}. {item}
            </div>
          ))}
        </div>
      </div>

      {/* People and Social Skills */}
      <div style={styles.skillBox}>
        <h3 style={styles.skillBoxTitle}>People and Social Skills</h3>
        <div style={{
          paddingLeft: "25px",
          margin: 0,
          fontSize: "14px",
          lineHeight: "1.6"
        }}>
          {[
            "Care, treat, heal, nurse or rehabilitate others",
            "Counsel, empower, coach, guide or listen to individuals",
            "Host, comfort, please, make welcome or serve customers",
            "Plan social, recreational or other group events",
            "Problem-solve, mediate or network with people",
            "Teach, train, instruct, inform or explain to groups",
            "Caring for children responsibly",
            "Caring for the sick and elderly",
            "Calming people down",
            "Helping people complete a task",
            "Knowing how to get along with different people/personalities",
            "Leading groups or activities"
          ].map((item, index) => (
            <div key={index} style={{ marginBottom: "8px", fontSize: "14px" }}>
              {index + 1}. {item}
            </div>
          ))}
        </div>
      </div>

      {/* Critical Thinking */}
      <div style={{
        ...styles.skillBox,
        marginBottom: "400px"
      }}>
        <h3 style={styles.skillBoxTitle}>Critical Thinking and Investigative Skills</h3>
        <div style={{
          paddingLeft: "25px",
          margin: 0,
          fontSize: "14px",
          lineHeight: "1.6"
        }}>
          {[
            "Analyze, use logic, problem solve, examine",
            "Conceptualize, adapt, develop, hypothesize or discover",
            "Evaluate, assess, test, appraise, diagnose",
            "Observe, reflect, study or notice",
            "Research, investigate, read or interview",
            "Synthesize, integrate, unify or conceptualize ideas"
          ].map((item, index) => (
            <div key={index} style={{ marginBottom: "8px", fontSize: "14px" }}>
              {index + 1}. {item}
            </div>
          ))}
        </div>
      </div>

      {/* Creative/Artistic Skills */}
      <div style={styles.skillBox}>
        <h3 style={styles.skillBoxTitle}>Creative/Artistic Skills</h3>
        <div style={{
          paddingLeft: "25px",
          margin: 0,
          fontSize: "14px",
          lineHeight: "1.6"
        }}>
          {[
            "Perceive intuitively, sense, show insight or have foresight",
            "Use artistic ability, photograph, decorate, paint or sculpt",
            "Use creativity, visualize, imagine, brainstorm or design",
            "Use musical ability, sing, compose or play instruments",
            "Presenting artistic ideas",
            "Visualizing shapes",
            "Designing",
            "Drawing, illustrating, sketching"
          ].map((item, index) => (
            <div key={index} style={{ marginBottom: "8px", fontSize: "14px" }}>
              {index + 1}. {item}
            </div>
          ))}
        </div>
      </div>

      {/* Communication Skills */}
      <div style={{
        ...styles.skillBox,
        marginBottom: "200px"
      }}>
        <h3 style={styles.skillBoxTitle}>Communication Skills</h3>
        <div style={{
          paddingLeft: "25px",
          margin: 0,
          fontSize: "14px",
          lineHeight: "1.6"
        }}>
          {[
            "Write, edit, translate, interpret or critique words",
            "Speak in public, debate, advocate, present or demonstrate an idea",
            "Facilitate a meeting Reading and following directions",
            "Comparing or cross-checking two lists",
            "Filling out forms",
            "Writing reports, letters and memos correctly",
            "Reading and understanding policies and memos",
            "Comfortably speaking to others, you do not know",
            "Taking notes while someone speaks",
            "Finding information Using a map",
            "Explaining things to other people",
            "Know when to ask for help or more explanation",
            "Counsel or advise others",
            "Listening to others"
          ].map((item, index) => (
            <div key={index} style={{ marginBottom: "8px", fontSize: "14px" }}>
              {index + 1}. {item}
            </div>
          ))}
        </div>
      </div>

      {/* Management Skills */}
      <div style={styles.skillBox}>
        <h3 style={styles.skillBoxTitle}>Management and Self-Management Skills</h3>
        <div style={{
          paddingLeft: "25px",
          margin: 0,
          fontSize: "14px",
          lineHeight: "1.6"
        }}>
          {[
            "Administer, set goals and priorities, plan or make decisions",
            "Initiate, assess needs, anticipate or create change",
            "Manage people, delegate tasks, direct, oversee or motivate",
            "Sell, negotiate, convince, promote or persuade",
            "Being patient with others",
            "Keeping a cheerful attitude",
            "Getting interested/excited about the task at hand",
            "Offering to help when it's needed",
            "Knowing how to take directions",
            "Motivating myself to do what needs to get done",
            "Helping motivates others to get the job done",
            "Prioritizing tasks so that the larger goal is met on time",
            "Following the rules",
            "Presenting a neat and professional image",
            "Checking your own work",
            "Using courtesy when dealing with others",
            "Seeking help when needed",
            "Being eager to learn",
            "Speaking up for yourself",
            "Solving problems in a cooperative way"
          ].map((item, index) => (
            <div key={index} style={{ marginBottom: "8px", fontSize: "14px" }}>
              {index + 1}. {item}
            </div>
          ))}
        </div>
      </div>
    </div>
  );

  return (
    <div id="pdf-content" style={styles.layout.container}>
      {/* Header Section */}
      <div style={styles.header.wrapper}>
        <div style={styles.header.topRow}>
          <img src={Logo} alt="Logo" style={styles.header.logo} />
          <div style={styles.header.candidateInfo}>
            <div>Assessed by Dr. Bharti Venkatesh</div>
            <div>Candidate ID: {userData.uniqueID}</div>
            <div>Candidate Name: {userData.firstName} {userData.lastName}</div>
          </div>
        </div>
        <h1 style={styles.header.title}>
          Psychometric Assessment Report
        </h1>
      </div>

      {/* Main Container - now single column */}
      <div style={styles.layout.mainContainer}>
        <h3 style={styles.section.title}>A. Your Personality</h3>
        
        {/* Personality Scores */}
        <p style={{ ...styles.scores.item, margin: "0 0 10px 0" }}>
          <strong>Personality scores:</strong>
        </p>
        
        <ul style={{ listStyleType: "none", padding: 0, marginBottom: "50px" }}>
          {["Extraversion", "Openness", "Agreeableness", "Neuroticism", "Conscientiousness"].map(trait => 
            <TraitScore key={trait} trait={trait} score={userData?.traits_scores?.[trait] ?? 0} />
          )}
        </ul>

        {/* Scoring Interpretation Table */}
        <table style={{
          ...styles.table.wrapper,
          marginBottom: "5px"
        }}>
          <thead>
            <tr>
              <th colSpan="2" style={styles.table.headerTitle}>
                Scoring Interpretation
              </th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td style={{
                borderRight: "2px solid #888",
                padding: "12px",
                fontSize: "16px"
              }}>
                1 = Low Range
              </td>
              <td style={{ 
                padding: "12px",
                fontSize: "16px"
              }}>
                The highest score will be 5 and lowest score would be 1
              </td>
            </tr>
            <tr>
              <td style={{
                borderRight: "2px solid #888",
                padding: "12px",
                fontSize: "16px"
              }}>
                2-3 = Moderate Range
              </td>
              <td style={{ padding: "12px", fontSize: "16px" }}></td>
            </tr>
            <tr>
              <td style={{
                borderRight: "2px solid #888",
                padding: "12px",
                fontSize: "16px"
              }}>
                4-5 = High Range
              </td>
              <td style={{ padding: "12px", fontSize: "16px" }}></td>
            </tr>
          </tbody>
        </table>

        {/* Render each trait section */}
        {Object.keys(traitContent).map(trait => renderTraitSection(trait))}

        {/* Interests Section */}
        <div style={styles.layout.newPage}>
          <h3 style={styles.section.title}>B. Interests Scores</h3>
          
          <ul style={styles.interests.list}>
            <li>
              <strong>Interest 1:</strong> {userData.interests_scores.topSection1.name}
            </li>
            <li>
              <strong>Interest 2:</strong> {userData.interests_scores.topSection2.name}
            </li>
            <li>
              <strong>Interest 3:</strong> {userData.interests_scores.topSection3.name}
            </li>
            <li>
              <strong>Interest 4:</strong> {userData.interests_scores.topSection4.name}
            </li>
            <li>
              <strong>Interest 5:</strong> {userData.interests_scores.topSection5.name}
            </li>
          </ul>

          <p style={styles.section.description}>
            <strong style={{ color: "#01A9BE" }}>Scoring Interpretation: </strong>
            Interest Assessment is divided into 16 groups. Each group is a career agglomeration. Career 
            agglomeration place similar occupations in groups. These in turn will help the individual to 
            narrow down the thousands of career options in the world to a general area of interest. Each 
            career agglomeration connects to what the individual has learned to the skills and knowledge 
            the individual need for the entry into the world of career.
          </p>
        </div>

        {/* Skills Section */}
        {renderSkillsOverview()}
        {["Human_Skill", "Technical_Skill", "Conceptual_Skill"].map(skill => 
          renderSkillSection(skill)
        )}

        {/* Encapsulation Section */}
        {renderEncapsulation()}
      </div>
    </div>
  );
};

export default PsychometricTestPDFLayout;
